import React from 'react';

// North Indian Diamond Style Kundli Chart
const NorthIndianChart = ({ 
  planets = {}, // { planetName: { house: 1, degree: 25.5, retrograde: false } }
  houseSignMap = {}, // { 1: 'Leo', 2: 'Virgo', ... }
  size = 320,
  title = '',
  showDegrees = false
}) => {
  
  // Planet abbreviations
  const planetAbbrev = {
    'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Ma', 'Mercury': 'Me',
    'Jupiter': 'Ju', 'Venus': 'Ve', 'Saturn': 'Sa', 'Rahu': 'Ra', 'Ketu': 'Ke',
    'Ascendant': 'As', 'Lagna': 'Lg'
  };

  // Sign abbreviations
  const signAbbrev = {
    'Aries': 'Ar', 'Taurus': 'Ta', 'Gemini': 'Ge', 'Cancer': 'Ca',
    'Leo': 'Le', 'Virgo': 'Vi', 'Libra': 'Li', 'Scorpio': 'Sc',
    'Sagittarius': 'Sg', 'Capricorn': 'Cp', 'Aquarius': 'Aq', 'Pisces': 'Pi'
  };

  // Planet colors
  const planetColors = {
    'Sun': '#F59E0B', 'Moon': '#E5E7EB', 'Mars': '#EF4444', 'Mercury': '#22C55E',
    'Jupiter': '#EAB308', 'Venus': '#EC4899', 'Saturn': '#3B82F6', 'Rahu': '#8B5CF6', 'Ketu': '#D97706',
    'Ascendant': '#F97316', 'Lagna': '#F97316'
  };

  // North Indian chart house positions (diamond layout)
  // House 1 is at top center, proceeds counter-clockwise
  const scale = size / 400;
  
  // House center positions for placing planet text
  const houseCenters = {
    1: { x: 200, y: 100 },   // Top center (12th position in diamond)
    2: { x: 100, y: 100 },   // Top left corner
    3: { x: 100, y: 200 },   // Left center
    4: { x: 100, y: 300 },   // Bottom left corner
    5: { x: 200, y: 300 },   // Bottom center
    6: { x: 300, y: 300 },   // Bottom right corner
    7: { x: 300, y: 200 },   // Right center
    8: { x: 300, y: 100 },   // Top right corner
    9: { x: 200, y: 50 },    // Top diamond
    10: { x: 50, y: 200 },   // Left diamond
    11: { x: 200, y: 350 },  // Bottom diamond
    12: { x: 350, y: 200 }   // Right diamond
  };

  // Alternative accurate North Indian positions
  const housePositions = {
    1: { x: 200, y: 85 },     // House 1 - Top center triangle
    2: { x: 85, y: 85 },      // House 2 - Top left
    3: { x: 85, y: 200 },     // House 3 - Left center
    4: { x: 85, y: 315 },     // House 4 - Bottom left
    5: { x: 200, y: 315 },    // House 5 - Bottom center
    6: { x: 315, y: 315 },    // House 6 - Bottom right
    7: { x: 315, y: 200 },    // House 7 - Right center
    8: { x: 315, y: 85 },     // House 8 - Top right
    9: { x: 140, y: 140 },    // House 9 - Inner top left
    10: { x: 140, y: 260 },   // House 10 - Inner bottom left
    11: { x: 260, y: 260 },   // House 11 - Inner bottom right
    12: { x: 260, y: 140 }    // House 12 - Inner top right
  };

  // Group planets by house
  const planetsByHouse = {};
  Object.entries(planets).forEach(([planet, data]) => {
    const house = data.house || data.bhava || 1;
    if (!planetsByHouse[house]) planetsByHouse[house] = [];
    planetsByHouse[house].push({
      name: planet,
      degree: data.degree || 0,
      retrograde: data.retrograde || false
    });
  });

  // Render planets in a house
  const renderPlanetsInHouse = (house) => {
    const planetsInHouse = planetsByHouse[house] || [];
    const pos = housePositions[house];
    if (!pos) return null;

    return planetsInHouse.map((planet, idx) => {
      const abbrev = planetAbbrev[planet.name] || planet.name.substring(0, 2);
      const color = planetColors[planet.name] || '#FFFFFF';
      const yOffset = idx * 14;
      const retro = planet.retrograde ? '(R)' : '';
      const degreeStr = showDegrees ? ` ${planet.degree?.toFixed(0)}°` : '';
      
      return (
        <text
          key={`${house}-${planet.name}`}
          x={pos.x * scale}
          y={(pos.y + yOffset) * scale}
          fill={color}
          fontSize={11 * scale}
          fontWeight="600"
          fontFamily="monospace"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {abbrev}{retro}{degreeStr}
        </text>
      );
    });
  };

  // Render sign in corner of house
  const renderHouseSign = (house) => {
    const sign = houseSignMap[house];
    if (!sign) return null;
    
    const pos = housePositions[house];
    if (!pos) return null;

    const abbrev = signAbbrev[sign] || sign.substring(0, 2);
    
    return (
      <text
        key={`sign-${house}`}
        x={(pos.x - 25) * scale}
        y={(pos.y - 25) * scale}
        fill="#6B7280"
        fontSize={9 * scale}
        fontFamily="sans-serif"
        textAnchor="middle"
      >
        {abbrev}
      </text>
    );
  };

  return (
    <div className="inline-block">
      {title && (
        <div className="text-center text-amber-400 text-sm font-semibold mb-2">{title}</div>
      )}
      <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        className="bg-gray-900/80 rounded-lg"
      >
        {/* Outer square border */}
        <rect
          x={10 * scale}
          y={10 * scale}
          width={380 * scale}
          height={380 * scale}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={2 * scale}
        />

        {/* Diamond in center (X shape) */}
        {/* Top-left to bottom-right diagonal */}
        <line
          x1={10 * scale} y1={10 * scale}
          x2={390 * scale} y2={390 * scale}
          stroke="#F59E0B"
          strokeWidth={1.5 * scale}
        />
        {/* Top-right to bottom-left diagonal */}
        <line
          x1={390 * scale} y1={10 * scale}
          x2={10 * scale} y2={390 * scale}
          stroke="#F59E0B"
          strokeWidth={1.5 * scale}
        />

        {/* Inner diamond */}
        <polygon
          points={`
            ${200 * scale},${10 * scale}
            ${390 * scale},${200 * scale}
            ${200 * scale},${390 * scale}
            ${10 * scale},${200 * scale}
          `}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={1.5 * scale}
        />

        {/* House numbers */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(house => {
          const pos = housePositions[house];
          if (!pos) return null;
          return (
            <text
              key={`house-num-${house}`}
              x={(pos.x + 30) * scale}
              y={(pos.y - 30) * scale}
              fill="#4B5563"
              fontSize={8 * scale}
              fontFamily="sans-serif"
            >
              {house}
            </text>
          );
        })}

        {/* Render signs for each house */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(house => renderHouseSign(house))}

        {/* Render planets */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(house => renderPlanetsInHouse(house))}

        {/* Lagna marker in center */}
        <text
          x={200 * scale}
          y={200 * scale}
          fill="#F59E0B"
          fontSize={10 * scale}
          fontWeight="bold"
          fontFamily="sans-serif"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          Lg
        </text>
      </svg>
    </div>
  );
};

export default NorthIndianChart;
